import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, AlertCircle, KeyRound, ArrowLeft, Loader2, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import Logo from '../components/Logo';
import { useClinic } from '@/context/ClinicContext';
import { SocialLoginSection } from '../components/GoogleSignInButton';

const LoginPage = ({ setUser }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { selectClinic } = useClinic();
  
  // 2FA state
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFactorUserId, setTwoFactorUserId] = useState(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [verifying2FA, setVerifying2FA] = useState(false);
  
  // Use localStorage for error persistence to survive context re-renders
  const [error, setError] = useState(() => {
    return sessionStorage.getItem('loginError') || '';
  });

  // Persist error to sessionStorage
  useEffect(() => {
    if (error) {
      sessionStorage.setItem('loginError', error);
    } else {
      sessionStorage.removeItem('loginError');
    }
  }, [error]);

  useEffect(() => {
    // Check if bypass parameter is present (for super admin)
    const bypassClinic = searchParams.get('bypass') === 'true';
    const logoutReason = searchParams.get('reason');
    
    if (bypassClinic) {
      // Clear clinic context for super admin login
      localStorage.removeItem('clinic_id');
      localStorage.removeItem('clinic_data');
      window.dispatchEvent(new Event('storage'));
    }
    
    // Show message if logged out due to inactivity
    if (logoutReason === 'idle') {
      toast.info('Session expired due to inactivity', {
        description: 'Please sign in again to continue.',
        duration: 5000
      });
      // Clean up URL
      window.history.replaceState({}, document.title, '/login');
    }
  }, [searchParams]);

  const completeLogin = (access_token, user) => {
    // Store token and user data
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(user));
    
    // Store clinic_id if user belongs to a clinic (not super_admin)
    if (user.clinic_id) {
      localStorage.setItem('clinic_id', user.clinic_id);
      selectClinic(user.clinic_id);
    }
    
    // Set first login flag for new users
    if (user.is_first_login && user.role === 'admin') {
      localStorage.setItem(`first_login_${user.id}`, 'true');
    }
    
    setUser(user);
    
    toast.success('Login successful!');
    // Redirect based on role
    const superAdminRoles = ['super_admin', 'super_owner', 'platform_admin', 'support_admin', 'content_admin'];
    const staffRoles = ['staff', 'receptionist', 'nurse', 'provider'];
    let redirectPath = '/admin';
    if (superAdminRoles.includes(user.role)) {
      redirectPath = '/super-admin';
    } else if (user.role === 'patient') {
      redirectPath = '/patient';
    } else if (staffRoles.includes(user.role)) {
      redirectPath = '/staff';
    }
    navigate(redirectPath);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    // Clear error when starting a new attempt
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      
      // Check if 2FA is required
      if (response.data.requires_2fa) {
        setRequires2FA(true);
        setTwoFactorUserId(response.data.user_id);
        setLoading(false);
        toast.info('Two-factor authentication required');
        return;
      }
      
      const { access_token, user } = response.data;
      completeLogin(access_token, user);
    } catch (error) {
      setLoading(false);
      const errorMessage = error.response?.data?.detail || 'Invalid email or password. Please try again.';
      const statusCode = error.response?.status;
      
      // Check if email is not verified (403)
      if (statusCode === 403 && errorMessage.includes('verify')) {
        setError('email_not_verified');
        toast.error('Please verify your email before signing in');
        return;
      }
      
      // Set error - will persist in sessionStorage
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handle2FAVerify = async (e) => {
    e.preventDefault();
    setError('');
    setVerifying2FA(true);

    try {
      const response = await axios.post(`${API}/auth/login/verify-2fa`, null, {
        params: {
          user_id: twoFactorUserId,
          code: twoFactorCode
        }
      });
      
      const { access_token, user, message } = response.data;
      
      // Show backup code warning if applicable
      if (message && message.includes('Backup code')) {
        toast.warning(message);
      }
      
      completeLogin(access_token, user);
    } catch (error) {
      setVerifying2FA(false);
      const errorMessage = error.response?.data?.detail || 'Invalid verification code. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleBack = () => {
    setRequires2FA(false);
    setTwoFactorUserId(null);
    setTwoFactorCode('');
    setError('');
  };

  // Check if this is super admin login
  const bypassClinic = searchParams.get('bypass') === 'true';

  // 2FA Verification Screen
  if (requires2FA) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader className="space-y-4">
            <div className="flex items-center justify-center space-x-3">
              <Logo className="h-16 w-16" />
              <div className="flex flex-col">
                <span className="text-3xl font-bold text-gray-900">MyMedMember</span>
                <span className="text-xs text-gray-500">by Clinicax Systems</span>
              </div>
            </div>
            <div className="flex flex-col items-center space-y-2">
              <div className="p-3 bg-teal-100 rounded-full">
                <KeyRound className="h-8 w-8 text-teal-600" />
              </div>
              <CardTitle className="text-2xl text-center">Two-Factor Authentication</CardTitle>
              <CardDescription className="text-center">
                Enter the 6-digit code from your authenticator app
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handle2FAVerify} className="space-y-4">
              {error && (
                <div className="p-4 mb-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-800 font-medium">{error}</p>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="twoFactorCode">Verification Code</Label>
                <Input
                  id="twoFactorCode"
                  type="text"
                  placeholder="000000"
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  maxLength={6}
                  className="text-center text-2xl tracking-widest font-mono"
                  autoFocus
                  autoComplete="one-time-code"
                />
                <p className="text-xs text-gray-500 text-center mt-2">
                  You can also use a backup code if you have lost access to your authenticator
                </p>
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-teal-600 hover:bg-teal-700" 
                disabled={verifying2FA || twoFactorCode.length < 6}
              >
                {verifying2FA ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify & Sign In'
                )}
              </Button>
              
              <Button 
                type="button"
                variant="ghost"
                className="w-full"
                onClick={handleBack}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Login
              </Button>
            </form>
            
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Having trouble?</h4>
              <ul className="text-xs text-gray-600 space-y-1">
                <li>• Make sure your authenticator app time is synced</li>
                <li>• Use one of your backup codes if you lost your device</li>
                <li>• Contact support if you cannot access your account</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Regular Login Screen
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50 flex items-center justify-center p-4">
      {/* Back to Home Link */}
      <Link 
        to="/" 
        className="absolute top-4 left-4 text-teal-600 hover:text-teal-700 flex items-center space-x-2"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m12 19-7-7 7-7"/>
          <path d="M19 12H5"/>
        </svg>
        <span>Back to Home</span>
      </Link>
      
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="space-y-4">
          <div className="flex items-center justify-center space-x-3">
            <Logo className="h-16 w-16" />
            <div className="flex flex-col">
              <span className="text-3xl font-bold text-gray-900">MyMedMember</span>
              <span className="text-xs text-gray-500">by Clinicax Systems</span>
            </div>
          </div>
          <CardTitle className="text-2xl text-center" data-testid="login-title">Welcome Back</CardTitle>
          <CardDescription className="text-center">
            Sign in to access your dashboard
          </CardDescription>
          {bypassClinic && (
            <div className="flex items-center justify-center text-sm text-gray-600 bg-purple-50 p-2 rounded-md">
              <Shield className="w-4 h-4 mr-2" />
              <span>Super Administrator Access</span>
            </div>
          )}
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            {error && error !== 'email_not_verified' && (
              <div className="p-4 mb-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800 font-medium">{error}</p>
              </div>
            )}
            
            {error === 'email_not_verified' && (
              <div className="p-4 mb-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2 mb-3">
                  <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-amber-800 font-medium">Email Not Verified</p>
                    <p className="text-xs text-amber-700 mt-1">Please verify your email before signing in. Check your inbox for the verification link.</p>
                  </div>
                </div>
                <Link 
                  to={`/verify-email?email=${encodeURIComponent(email)}`}
                  className="inline-flex items-center text-sm text-amber-700 hover:text-amber-900 font-medium underline"
                >
                  Resend verification email →
                </Link>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-teal-600 hover:text-teal-700 hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pr-10"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  data-testid="toggle-login-password-visibility"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button 
              type="submit" 
              className="w-full bg-teal-600 hover:bg-teal-700" 
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>
          
          {/* Divider */}
          {/* Social Login - Only shows if OAuth is configured */}
          <SocialLoginSection />
          
          <div className="mt-6 space-y-2">
            <div className="text-center text-sm">
              <span className="text-gray-600">Do not have an account? </span>
              <Link to="/register" className="text-teal-600 hover:text-teal-700 font-medium" data-testid="login-register-link">
                Register here
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
